import { useEffect, useState } from 'react';
import { getAllOrders, updateOrderStatus } from '../../services/api';
import toast from 'react-hot-toast';

const statusColors = {
  pending:    'bg-yellow-100 text-yellow-700',
  confirmed:  'bg-blue-100 text-blue-700',
  processing: 'bg-purple-100 text-purple-700',
  shipped:    'bg-indigo-100 text-indigo-700',
  delivered:  'bg-green-100 text-green-700',
  cancelled:  'bg-red-100 text-red-700',
};

const statuses = ['pending','confirmed','processing','shipped','delivered','cancelled'];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    getAllOrders().then(r => setOrders(r.data)).finally(() => setLoading(false));
  }, []);

  const handleStatusChange = async (orderId, status) => {
    setUpdating(orderId);
    try {
      await updateOrderStatus(orderId, { status });
      setOrders(prev => prev.map(o => o._id === orderId ? { ...o, orderStatus: status } : o));
      toast.success('Order status updated');
    } catch {
      toast.error('Update failed');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-display text-4xl font-bold text-yamass-black mb-8">Orders</h1>

      {loading ? (
        <div className="space-y-3">{[...Array(6)].map((_, i) => <div key={i} className="skeleton h-20 rounded-xl" />)}</div>
      ) : (
        <div className="bg-yamass-cream rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-yamass-black text-white">
                  <th className="text-left px-5 py-4 font-medium">Order ID</th>
                  <th className="text-left px-5 py-4 font-medium hidden md:table-cell">Customer</th>
                  <th className="text-left px-5 py-4 font-medium">Total</th>
                  <th className="text-left px-5 py-4 font-medium hidden md:table-cell">Payment</th>
                  <th className="text-left px-5 py-4 font-medium">Status</th>
                  <th className="text-left px-5 py-4 font-medium hidden lg:table-cell">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {orders.map(order => (
                  <tr key={order._id} className="hover:bg-white/50 transition-colors">
                    <td className="px-5 py-4 font-mono text-xs text-gray-500">
                      #{order._id.slice(-8).toUpperCase()}
                    </td>
                    <td className="px-5 py-4 text-gray-700 hidden md:table-cell">
                      <p className="font-medium">{order.user?.name || 'N/A'}</p>
                      <p className="text-xs text-gray-400">{order.user?.email}</p>
                    </td>
                    <td className="px-5 py-4 font-bold text-yamass-black">
                      ৳{order.totalPrice.toLocaleString()}
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell">
                      <span className={`text-xs px-2 py-1 rounded-full capitalize ${order.isPaid ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {order.isPaid ? `Paid · ${order.paymentMethod}` : `Unpaid · ${order.paymentMethod}`}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={order.orderStatus}
                        disabled={updating === order._id}
                        onChange={e => handleStatusChange(order._id, e.target.value)}
                        className={`text-xs font-medium px-3 py-1.5 rounded-full border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-yamass-accent ${statusColors[order.orderStatus]}`}
                      >
                        {statuses.map(s => <option key={s} value={s} className="bg-white text-gray-700">{s}</option>)}
                      </select>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-400 hidden lg:table-cell">
                      {new Date(order.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {orders.length === 0 && <p className="text-center py-12 text-gray-400">No orders yet.</p>}
          </div>
        </div>
      )}
    </div>
  );
}
